import { useMemo, useState } from "react";
import type { UserRow, FunnelStep } from "../sql";
import { FUNNEL_STEPS } from "../sql";
import { fmtPct, fmtN, pctVal } from "../../../lib/funnel/helpers";
import { InfoButton } from "../../../lib/funnel/InfoButton";
import { SectionCardHeader } from "../../../components/ChartCard";
import type { FilterPillSpec } from "../../../components/FilterPills";

// Single sorted table per stage:
//   - reach %         : free users who hit the stage
//   - paid conversion : % of stage-hitters who eventually paid
//   - lift vs baseline: paid conversion among hitters / paid conversion overall
//   - pre-paid timing : same lift but only counting users where the stage's
//                       first-event date precedes the subscription start date.
//                       Separates "feature precedes paying" from "paid users
//                       use it more once they're in".
// Computed entirely client-side from the existing distinct-user rowset.

type LiftRow = {
  step: FunnelStep;
  reachers: number;
  paidAmongReachers: number;
  liftVsBaseline: number;
  prePaidReachers: number;
  prePaidPaidConversion: number;
};

type SortKey = "stage" | "reach" | "conversion" | "lift" | "prePaid";

export function FeatureLiftTable({ users, pagePills }: { users: UserRow[]; pagePills?: FilterPillSpec[] }) {
  const [sortKey, setSortKey] = useState<SortKey>("prePaid");
  const stats = useMemo(() => {
    const total = users.length;
    const totalPaid = users.filter((u) => u.on_paid_plan).length;
    const baselinePaidPct = pctVal(totalPaid, total);

    const rows: LiftRow[] = FUNNEL_STEPS
      .filter((s) => s.key !== "paid")
      .map((step) => {
        const reachers = users.filter((u) => step.predicate(u));
        const reach = reachers.length;
        const paidAmongReachers = reachers.filter((u) => u.on_paid_plan).length;
        const conv = pctVal(paidAmongReachers, reach);
        const lift = baselinePaidPct === 0 ? 0 : conv / baselinePaidPct;

        // Pre-paid: stage's first-event date strictly precedes the user's
        // subscription start. Excludes users who hit the stage only after
        // paying — that's where "paid users use this more" creeps in.
        const prePaid = users.filter((u) => {
          if (!u.on_paid_plan) return false;
          if (!u.subscription_started_date_utc) return false;
          const first = step.firstDateOf(u);
          if (!first) return false;
          return first < u.subscription_started_date_utc;
        });
        const prePaidReachers = users.filter((u) => {
          const first = step.firstDateOf(u);
          if (!first) return false;
          if (!u.on_paid_plan) return true; // free users who hit the stage qualify by default
          // Paid users without a recorded subscription_started_date_utc can't be
          // classified pre/post-pay — exclude from both numerator and denominator
          // so they don't silently deflate the ratio.
          if (!u.subscription_started_date_utc) return false;
          return first < u.subscription_started_date_utc;
        }).length;
        const prePaidConv = pctVal(prePaid.length, prePaidReachers);

        return { step, reachers: reach, paidAmongReachers, liftVsBaseline: lift, prePaidReachers, prePaidPaidConversion: prePaidConv };
      });

    return { rows, total, totalPaid, baselinePaidPct };
  }, [users]);

  const sorted = useMemo(() => {
    const arr = [...stats.rows];
    arr.sort((a, b) => {
      switch (sortKey) {
        case "stage": return a.step.label.localeCompare(b.step.label);
        case "reach": return b.reachers - a.reachers;
        case "conversion": return (b.paidAmongReachers / Math.max(1, b.reachers)) - (a.paidAmongReachers / Math.max(1, a.reachers));
        case "lift": return b.liftVsBaseline - a.liftVsBaseline;
        case "prePaid": return b.prePaidPaidConversion - a.prePaidPaidConversion;
      }
    });
    return arr;
  }, [stats.rows, sortKey]);

  const pills: FilterPillSpec[] = [...(pagePills ?? [])];
  pills.push({ kind: "mode", label: `Sort: ${sortKey === "prePaid" ? "pre-paid conversion" : sortKey === "lift" ? "lift vs baseline" : sortKey === "conversion" ? "paid conversion" : sortKey === "reach" ? "reachers" : "stage"}` });

  return (
    <section className="card">
      <SectionCardHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            Feature to paid lift
            <InfoButton label="How to read this">
              <p style={{ margin: "0 0 6px" }}>
                For each funnel stage: how many users reach it, what % of reachers eventually convert to paid, and the lift
                vs the cohort-wide paid baseline ({fmtPct(stats.totalPaid, stats.total)}).
              </p>
              <p style={{ margin: "0 0 6px" }}>
                <strong>Pre-paid conversion</strong> only counts stage-hitters whose first event predates their subscription start.
                It separates &ldquo;feature precedes paying&rdquo; from &ldquo;paid users use it more once they&rsquo;re in&rdquo;; the higher this is,
                the more the stage looks like a leading indicator rather than a downstream one.
              </p>
              <p style={{ margin: 0, color: "var(--fg-3)" }}>
                Calendar typically flips negative on pre-paid lift; it&rsquo;s a post-purchase step, not a leading indicator.
              </p>
            </InfoButton>
          </span>
        }
        subtitle={`Baseline paid conversion: ${fmtPct(stats.totalPaid, stats.total)} (${fmtN(stats.totalPaid)} / ${fmtN(stats.total)} users).`}
        filterPills={pills}
      />
      <div className="table-wrap">
        <table className="t" style={{ width: "100%", fontSize: 12 }}>
          <thead>
            <tr>
              <SortableTh label="Stage" k="stage" sortKey={sortKey} onSort={setSortKey} align="left" />
              <SortableTh label="Reachers" k="reach" sortKey={sortKey} onSort={setSortKey} />
              <th style={{ textAlign: "right" }}>Reach %</th>
              <SortableTh label="Paid conversion" k="conversion" sortKey={sortKey} onSort={setSortKey} />
              <SortableTh label="Lift vs baseline" k="lift" sortKey={sortKey} onSort={setSortKey} />
              <SortableTh label="Pre-paid conversion" k="prePaid" sortKey={sortKey} onSort={setSortKey} />
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => {
              const liftBg = r.liftVsBaseline > 1.5 ? "rgba(47, 122, 138, 0.12)" : r.liftVsBaseline < 0.8 ? "rgba(220, 106, 76, 0.10)" : undefined;
              return (
                <tr key={r.step.key}>
                  <td>{r.step.label}</td>
                  <td style={{ textAlign: "right" }}>{fmtN(r.reachers)}</td>
                  <td style={{ textAlign: "right" }}>{fmtPct(r.reachers, stats.total)}</td>
                  <td style={{ textAlign: "right" }}>
                    {fmtPct(r.paidAmongReachers, r.reachers)}
                    <span style={{ color: "var(--fg-3)", marginLeft: 6, fontSize: 10 }}>
                      {fmtN(r.paidAmongReachers)} / {fmtN(r.reachers)}
                    </span>
                  </td>
                  <td style={{ textAlign: "right", background: liftBg, fontWeight: 600 }}>
                    {r.liftVsBaseline.toFixed(2)}×
                  </td>
                  <td style={{ textAlign: "right" }}>{r.prePaidPaidConversion.toFixed(1)}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SortableTh({
  label, k, sortKey, onSort, align = "right",
}: {
  label: string; k: SortKey; sortKey: SortKey; onSort: (k: SortKey) => void; align?: "left" | "right";
}) {
  const active = sortKey === k;
  return (
    <th
      onClick={() => onSort(k)}
      style={{ textAlign: align, cursor: "pointer", color: active ? "var(--color-teal)" : undefined }}
    >
      {label}{active ? " ▼" : ""}
    </th>
  );
}
